import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const REGOLE_PASSWORD = [
  { test: (v) => v.length >= 8, label: "Almeno 8 caratteri" },
  { test: (v) => /[A-Z]/.test(v), label: "Almeno una lettera maiuscola" },
  { test: (v) => /[a-z]/.test(v), label: "Almeno una lettera minuscola" },
  { test: (v) => /[0-9]/.test(v), label: "Almeno un numero" },
  { test: (v) => /[^A-Za-z0-9]/.test(v), label: "Almeno un carattere speciale" },
];

export function RegolePassword({ valore }) {
  return (
    <ul className="space-y-1 mt-2" data-testid="regole-password">
      {REGOLE_PASSWORD.map((r) => {
        const ok = r.test(valore || "");
        return (
          <li key={r.label} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-slate-400"}`}>
            {ok ? <Check size={13} /> : <X size={13} />} {r.label}
          </li>
        );
      })}
    </ul>
  );
}

const inp = "w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm bg-white text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-200";
const lbl = "block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1";

/** Schermata bloccante al primo accesso: cambio password obbligatorio. */
export default function CambiaPassword() {
  const { user, setUser, logout } = useAuth();
  const [attuale, setAttuale] = useState("");
  const [nuova, setNuova] = useState("");
  const [conferma, setConferma] = useState("");
  const [busy, setBusy] = useState(false);
  const valida = REGOLE_PASSWORD.every((r) => r.test(nuova)) && nuova === conferma && attuale;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/password", { password_attuale: attuale, password_nuova: nuova });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password aggiornata: benvenuto");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4" data-testid="cambia-password-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-7">
        <div className="flex items-center gap-2 text-sky-700">
          <ShieldCheck size={18} />
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold">Primo accesso</span>
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 mt-2">Imposta una nuova password</h1>
        <p className="text-sm text-slate-500 mt-1">
          Per sicurezza la password iniziale di {user?.email} deve essere sostituita.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className={lbl}>Password attuale</label>
            <input className={inp} type="password" value={attuale} data-testid="cp-attuale"
              onChange={(e) => setAttuale(e.target.value)} />
          </div>
          <div>
            <label className={lbl}>Nuova password</label>
            <input className={inp} type="password" value={nuova} data-testid="cp-nuova"
              onChange={(e) => setNuova(e.target.value)} />
            <RegolePassword valore={nuova} />
          </div>
          <div>
            <label className={lbl}>Conferma nuova password</label>
            <input className={inp} type="password" value={conferma} data-testid="cp-conferma"
              onChange={(e) => setConferma(e.target.value)} />
            {conferma && conferma !== nuova && (
              <p className="text-xs text-red-600 mt-1">Le due password non coincidono</p>
            )}
          </div>
          <button type="submit" disabled={!valida || busy} data-testid="cp-salva"
            className="w-full px-5 py-2.5 rounded-md bg-sky-600 text-white text-sm font-semibold
              hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
            {busy ? "Salvataggio…" : "Salva e continua"}
          </button>
          <button type="button" onClick={logout} data-testid="cp-esci"
            className="w-full text-xs text-slate-400 hover:text-slate-700 transition-colors duration-200">
            Esci
          </button>
        </div>
      </form>
    </div>
  );
}
